import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Trash2,
  Upload,
  XCircle,
  ChevronDown,
  X,
  Save,
  Search,
  Filter,
  ArrowUpDown,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import api from "../api/api";
import MainLayout from "../sidebar/Sidebar.js";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { AIInput } from "../components/ui/ai-input";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import CatalogMatchesPopup from "../components/CatalogMatchesPopup";
import { hasAnyMatch } from "../utils/catalogSearch";

// Store refs for catalog match buttons to position popovers
const catalogButtonRefs = new Map();

// Layout expansion animation duration (in seconds for framer-motion, converted to ms for timeouts)
const LAYOUT_ANIMATION_DURATION = 0.7;

export default function EstimateNew() {
  const [input, setInput] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [error, setError] = useState("");
  const [location, setLocation] = useState("Estimate");
  const [existingProjects, setExistingProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [isCreatingNewProject, setIsCreatingNewProject] = useState(false);
  const [isTypingInAI, setIsTypingInAI] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    name: "",
    code: "",
    quantity: "",
    unit: "",
    unitPrice: "",
    category: "",
  });
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  
  // Animation state for flying items
  const [flyingItems, setFlyingItems] = useState([]);
  const aiInputRef = useRef(null);
  const tableRef = useRef(null);
  const tableHeaderRef = useRef(null);
  
  // Manual entry catalog popup state
  const [showManualEntryCatalogPopup, setShowManualEntryCatalogPopup] = useState(false);
  const manualEntryInputRef = useRef(null);
  const manualEntryTriggerRef = useRef(null);
  
  // Track if the input row is focused (any field)
  const [isInputRowActive, setIsInputRowActive] = useState(false);
  const inputRowRef = useRef(null);
  
  // Track if the layout has expanded (table and chatbox moved to final positions)
  const [hasExpandedLayout, setHasExpandedLayout] = useState(false);
  
  // Track if user has clicked Generate (to show AI disclaimer)
  const [hasGenerated, setHasGenerated] = useState(false);
  
  // Search term state
  const [searchTerm, setSearchTerm] = useState("");
  
  // Cost Catalog state for matching AI-generated items
  const [activeCatalogPopupIndex, setActiveCatalogPopupIndex] = useState(null);
  const [itemsWithMatches, setItemsWithMatches] = useState(new Set());
  const [isAnimating, setIsAnimating] = useState(false);

  const projectOptions = existingProjects.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await api.get("/projects/");
        setExistingProjects(res.data);
      } catch (err) {
        console.error("Failed to fetch existing projects", err);
      }
    }
    fetchProjects();
  }, []);

  // Fetch cost catalog for matching AI-generated items
  const { data: costCatalog = [] } = useQuery({
    queryKey: ["costCatalog"],
    queryFn: async () => {
      const response = await api.get("/cost-catalog/");
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: "always", // Always refetch when user returns to tab
  });

  // Compute matches for line items when they change or catalog changes
  const computeMatches = useCallback(() => {
    if (costCatalog.length === 0) return;
    
    const matchSet = new Set();
    lineItems.forEach((item, index) => {
      if (item.source === "ai" && hasAnyMatch(item.name, costCatalog)) {
        matchSet.add(index);
      }
    });
    setItemsWithMatches(matchSet);
  }, [lineItems, costCatalog]);

  // Recompute matches when line items or catalog changes (but skip during animation)
  useEffect(() => {
    if (isAnimating) return;
    computeMatches();
  }, [computeMatches, isAnimating]);

  // Handle catalog item selection - apply values to line item
  const handleCatalogSelect = useCallback((catalogItem, lineItemIndex) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[lineItemIndex] = {
        ...updated[lineItemIndex],
        name: catalogItem.name || updated[lineItemIndex].name,
        code: catalogItem.code || updated[lineItemIndex].code,
        quantity: catalogItem.default_quantity || updated[lineItemIndex].quantity,
        unit: catalogItem.unit || updated[lineItemIndex].unit,
        unitPrice: catalogItem.unit_price || updated[lineItemIndex].unitPrice,
        category: catalogItem.category || updated[lineItemIndex].category,
      };
      return updated;
    });
    setActiveCatalogPopupIndex(null);
  }, []);

  // Handle manual entry catalog selection - apply values from catalog item
  const handleManualEntryCatalogSelect = useCallback((catalogItem) => {
    setManualEntry({
      name: catalogItem.name || "",
      code: catalogItem.code || "",
      quantity: catalogItem.default_quantity ? String(catalogItem.default_quantity) : "",
      unit: catalogItem.unit || "",
      unitPrice: catalogItem.unit_price || "",
      category: catalogItem.category || "",
    });
    setShowManualEntryCatalogPopup(false);
  }, []);

  // Add manual entry handler - defined before the effect that uses it
  const handleAddManualEntry = useCallback(() => {
    if (!manualEntry.name.trim()) return;

    setLineItems((prev) => [
      ...prev,
      {
        name: manualEntry.name,
        code: manualEntry.code,
        quantity: manualEntry.quantity
          ? Number.parseInt(manualEntry.quantity, 10)
          : 0,
        unit: manualEntry.unit,
        unitPrice: manualEntry.unitPrice,
        category: manualEntry.category,
        source: "manual",
      },
    ]);

    setManualEntry({
      name: "",
      code: "",
      quantity: "",
      unit: "",
      unitPrice: "",
      category: "",
    });
  }, [manualEntry]);

  // Handle input row blur for auto-save when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle input row blur - auto-save if clicking outside the input row
      // Note: catalog popup close is handled by the popup's own backdrop click
      if (inputRowRef.current && !inputRowRef.current.contains(event.target)) {
        // Check if we have a name (required to save) and row was active
        if (manualEntry.name.trim() && !showManualEntryCatalogPopup) {
          handleAddManualEntry();
        }
        setIsInputRowActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [manualEntry, handleAddManualEntry, showManualEntryCatalogPopup]);

  const handleDelete = (index) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
    // Also remove from selected items
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      // Adjust indices for items after the deleted one
      const adjusted = new Set();
      newSet.forEach((i) => {
        if (i > index) {
          adjusted.add(i - 1);
        } else {
          adjusted.add(i);
        }
      });
      return adjusted;
    });
  };

  const handleToggleSelect = (index) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedItems.size === lineItems.length && lineItems.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(lineItems.map((_, i) => i)));
    }
  };

  const handleEditItem = (index) => {
    setEditingIndex(index);
    setEditingItem({ ...lineItems[index] });
    setShowEditPanel(true);
  };

  const handleSaveEdit = () => {
    if (!editingItem.name.trim()) {
      setError("Line item name cannot be empty");
      return;
    }

    const updatedItems = [...lineItems];
    updatedItems[editingIndex] = {
      ...editingItem,
      quantity: editingItem.quantity
        ? Number.parseInt(editingItem.quantity, 10)
        : 0,
    };
    setLineItems(updatedItems);
    setShowEditPanel(false);
    setEditingIndex(null);
    setEditingItem(null);
    setError("");
  };

  const handleCancelEdit = () => {
    setShowEditPanel(false);
    setEditingIndex(null);
    setEditingItem(null);
    setError("");
  };

  // Reusable animation function to animate items from AI input to table
  const animateItemsToTable = useCallback(async (aiItems) => {
    const aiInputRect = aiInputRef.current?.getBoundingClientRect();
    const tableRect = tableRef.current?.getBoundingClientRect();
    const headerCells = tableHeaderRef.current?.querySelectorAll('th');
    
    // Get column positions from header cells
    const columnPositions = {};
    if (headerCells) {
      const headerArray = Array.from(headerCells);
      const columnMapping = ['checkbox', 'name', 'code', 'quantity', 'unit', 'unitPrice', 'category', 'total', 'actions'];
      headerArray.forEach((cell, idx) => {
        if (columnMapping[idx] && columnMapping[idx] !== 'checkbox' && columnMapping[idx] !== 'actions' && columnMapping[idx] !== 'total') {
          const rect = cell.getBoundingClientRect();
          columnPositions[columnMapping[idx]] = {
            x: rect.left + rect.width / 2,
            width: rect.width,
          };
        }
      });
    }
    
    if (aiInputRect && tableRect && aiItems.length > 0 && Object.keys(columnPositions).length > 0) {
      // Process items in batches of 3
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < aiItems.length; i += batchSize) {
        batches.push(aiItems.slice(i, i + batchSize));
      }
      
      let currentLineItemsCount = lineItems.length;
      const rowHeight = 56;
      const headerHeight = 80;
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Scroll the table to make room for the incoming batch
        if (tableRef.current) {
          const tableContainer = tableRef.current;
          const visibleHeight = tableContainer.getBoundingClientRect().height - headerHeight;
          const currentContentHeight = (currentLineItemsCount + 3) * rowHeight;
          const newContentHeight = currentContentHeight + batch.length * rowHeight;
          
          if (newContentHeight > visibleHeight + tableContainer.scrollTop) {
            const scrollTarget = newContentHeight - visibleHeight + rowHeight;
            tableContainer.scrollTo({ top: scrollTarget, behavior: 'smooth' });
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
        
        // Recalculate column positions after scroll
        const currentTableRect = tableRef.current?.getBoundingClientRect();
        const currentColumnPositions = {};
        const currentHeaderCells = tableHeaderRef.current?.querySelectorAll('th');
        if (currentHeaderCells) {
          const columnMapping = ['checkbox', 'name', 'code', 'quantity', 'unit', 'unitPrice', 'category', 'total', 'actions'];
          Array.from(currentHeaderCells).forEach((cell, idx) => {
            if (columnMapping[idx] && !['checkbox', 'actions', 'total'].includes(columnMapping[idx])) {
              const rect = cell.getBoundingClientRect();
              currentColumnPositions[columnMapping[idx]] = { x: rect.left + rect.width / 2 };
            }
          });
        }
        
        // Calculate positions
        const tableTop = currentTableRect?.top || tableRect.top;
        const scrollTop = tableRef.current?.scrollTop || 0;
        
        // Create flying items for this batch
        const batchFlyingItems = batch.flatMap((item, itemIndexInBatch) => {
          const baseDelay = itemIndexInBatch * 0.1;
          const rowPositionInTable = currentLineItemsCount + itemIndexInBatch;
          const targetY = tableTop + headerHeight + (rowPositionInTable * rowHeight) - scrollTop + (rowHeight / 2);
          
          const fields = [
            { field: 'name', label: item.name },
            { field: 'code', label: item.code || '-' },
            { field: 'quantity', label: item.quantity || '0' },
            { field: 'unit', label: item.unit || '-' },
            { field: 'unitPrice', label: item.unitPrice ? `$${item.unitPrice}` : '$0.00' },
            { field: 'category', label: item.category || 'General' },
          ].filter(f => currentColumnPositions[f.field]);
          
          return fields.map((fieldData, fieldIndex) => ({
            id: `flying-${Date.now()}-${batchIndex}-${itemIndexInBatch}-${fieldData.field}`,
            label: fieldData.label,
            startX: aiInputRect.left + aiInputRect.width / 2,
            startY: aiInputRect.top + 30,
            endX: currentColumnPositions[fieldData.field].x,
            endY: targetY,
            delay: baseDelay + fieldIndex * 0.03,
            isName: fieldData.field === 'name',
          }));
        });
        
        // Animate batch
        setFlyingItems(batchFlyingItems);
        await new Promise((resolve) => setTimeout(resolve, 650));
        
        // Add batch to table and clear flying items
        setLineItems((prev) => [...prev, ...batch]);
        currentLineItemsCount += batch.length;
        setFlyingItems([]);
      }
      return true; // Animation was performed
    }
    return false; // Animation was not performed (fallback needed)
  }, [lineItems.length]);

  // Handle voice recording completion - transcribe and generate in a single request
  const handleVoiceRecordingComplete = async (audioBlob) => {
    setIsTranscribing(true);
    setError("");
    setExportMessage("");
    setHasGenerated(true);
    
    // Expand layout when processing voice
    if (!hasExpandedLayout) {
      setHasExpandedLayout(true);
    }

    try {
      // Create FormData to send audio file
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");

      // Transcribe and generate in a single request (eliminates round trip)
      const res = await api.post("/transcribe-and-generate", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const items = res.data.line_items || [];
      const aiItems = items.map((item) => ({ ...item, source: "ai" }));
      
      setIsTranscribing(false);
      setLoading(true);
      setIsAnimating(true);
      
      // Capture offset BEFORE animation (items will land at these indices)
      const offset = lineItems.length;
      
      // Compute matches for new items (runs before animation visually starts)
      const newMatchIndices = [];
      aiItems.forEach((item, index) => {
        if (hasAnyMatch(item.name, costCatalog)) {
          newMatchIndices.push(offset + index);
        }
      });
      
      // Animate items to table (same animation as text input)
      const animated = await animateItemsToTable(aiItems);
      if (!animated) {
        // Fallback: just add items without animation
        setLineItems((prev) => [...prev, ...aiItems]);
      }
      
      // After animation: show catalog match buttons
      setItemsWithMatches((prev) => {
        const newSet = new Set(prev);
        newMatchIndices.forEach((idx) => newSet.add(idx));
        return newSet;
      });
      
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to process voice input. Please try again."
      );
    } finally {
      setIsTranscribing(false);
      setIsAnimating(false);
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    setExportMessage("");
    setIsTypingInAI(false);
    setHasGenerated(true);
    setIsAnimating(true);
    
    // Expand layout when generating
    if (!hasExpandedLayout) {
      setHasExpandedLayout(true);
    }

    try {
      const res = await api.post("/generate-estimate", { input_text: input });
      const items = res.data.line_items || [];

      const aiItems = items.map((item) => ({ ...item, source: "ai" }));

      // Capture offset BEFORE animation (items will land at these indices)
      const offset = lineItems.length;
      
      // Compute matches for new items (runs before animation visually starts)
      const newMatchIndices = [];
      aiItems.forEach((item, index) => {
        if (hasAnyMatch(item.name, costCatalog)) {
          newMatchIndices.push(offset + index);
        }
      });

      // Clear the input immediately for visual feedback
      setInput("");
      
      // Animate items to table
      const animated = await animateItemsToTable(aiItems);
      if (!animated) {
        // Fallback: just add items without animation
        setLineItems((prev) => [...prev, ...aiItems]);
      }
      
      // After animation: show catalog match buttons
      setItemsWithMatches((prev) => {
        const newSet = new Set(prev);
        newMatchIndices.forEach((idx) => newSet.add(idx));
        return newSet;
      });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to generate estimate. Please try again."
      );
    } finally {
      setIsAnimating(false);
      setLoading(false);
    }
  };

  const handleResetAll = () => {
    setLineItems([]);
    setInput("");
    setManualEntry({
      name: "",
      code: "",
      quantity: "",
      unit: "",
      unitPrice: "",
      category: "",
    });
    setShowManualEntryCatalogPopup(false);
    setError("");
    setExportMessage("");
  };

  const handleExport = async () => {
    setExportLoading(true);
    setExportMessage("");
    setError("");

    try {
      let projectNameToSend = "";

      if (isCreatingNewProject) {
        if (!projectName.trim()) {
          setError("Please enter a new project name.");
          setExportLoading(false);
          return;
        }
        projectNameToSend = projectName.trim();
      } else {
        const selectedProject = existingProjects.find(
          (proj) => proj.id === selectedProjectId
        );

        if (!selectedProject) {
          setError("Please select a valid project to export.");
          setExportLoading(false);
          return;
        }
        projectNameToSend = selectedProject.name;
      }

      const res = await api.post("/export-line-items", {
        project_name: projectNameToSend,
        line_items: lineItems,
      });

      if (res.data.success) {
        setExportMessage(
          `Line items added successfully to project "${projectNameToSend}"`
        );
        handleResetAll();
        setShowExportModal(false);
        setProjectName("");
        setSelectedProjectId("");
        setIsCreatingNewProject(false);
      } else {
        setError(res.data.error || "Failed to export line items, try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.detail || "Failed to export line items, try again."
      );
    } finally {
      setExportLoading(false);
    }
  };


  const isManualEntryDisabled = loading;


  const aiItemCount = lineItems.filter((item) => item.source === "ai").length;
  const manualItemCount = lineItems.filter(
    (item) => item.source === "manual"
  ).length;

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="flex flex-col h-full overflow-hidden" style={{ backgroundColor: 'rgba(0, 109, 226, 0)' }}>
        {/* Content area - centered initially, glides up on interaction */}
        <motion.div 
          className="flex-1 overflow-hidden py-6 px-5 min-h-0"
          initial={false}
          animate={{
            y: hasExpandedLayout ? 0 : 'calc(25vh - 50px)',
          }}
          transition={{
            duration: LAYOUT_ANIMATION_DURATION,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          <div className="h-full flex flex-col overflow-hidden">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm mb-6" style={{ color: '#1C1C1C' }}>
              {error}
            </div>
          )}
          {exportMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm mb-6" style={{ color: '#1C1C1C' }}>
              {exportMessage}
            </div>
          )}

          <div className="min-h-0 flex-1 flex flex-col overflow-hidden" style={{ maxHeight: '100%' }}>
            <div className="flex items-center justify-between gap-3 pb-3 flex-shrink-0">
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: "#727272" }} />
                  <input
                    type="text"
                    placeholder="Search here..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 rounded-lg bg-white text-sm shadow-sm focus:outline-none focus:ring-0"
                    style={{ border: "1px solid #e8e8e8", color: "#727272", padding: "7px 11px", paddingLeft: "40px" }}
                  />
                </div>

                {/* Sort + Filter */}
                <Button variant="customStandard" size="customStandard">
                  <ArrowUpDown className="w-4 h-4" />
                  Sort
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="customStandard" size="customStandard">
                  <Filter className="w-4 h-4" />
                  Filter
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-3">
              {lineItems.length > 0 && (
                <>
                  <Button variant="customStandard" size="customStandard" onClick={handleResetAll}>
                    <XCircle className="w-4 h-4" />
                    Reset All
                  </Button>
                  <Button variant="customStandard" size="customStandard" onClick={() => setShowExportModal(true)}>
                    <Upload className="w-4 h-4" />
                    Export All
                  </Button>
                </>
              )}
              <Button
                variant="customStandard"
                size="customStandard"
                onClick={() => {
                  /* Import functionality to be implemented */
                }}
              >
                <Upload className="w-4 h-4" />
                Import Template
              </Button>
              </div>
            </div>
            {lineItems.length > 0 && (
              <div className="flex items-center gap-2 pb-3 flex-shrink-0">
                <span className="text-xs bg-gray-100 px-2 py-1 rounded-md" style={{ color: '#1C1C1C' }}>
                  {lineItems.length} total
                </span>
                {aiItemCount > 0 && (
                  <span className="text-xs bg-blue-50 px-2 py-1 rounded-md" style={{ color: '#1C1C1C' }}>
                    {aiItemCount} AI
                  </span>
                )}
                {manualItemCount > 0 && (
                  <span className="text-xs bg-green-50 px-2 py-1 rounded-md" style={{ color: '#1C1C1C' }}>
                    {manualItemCount} manual
                  </span>
                )}
              </div>
            )}
            {/* This is where table border and shadow are defined */}
            <div ref={tableRef} className="min-h-0 overflow-y-auto rounded-2xl" style={{ boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.02), 0 -2px 12px -4px rgba(0, 0, 0, 0.02), 0 4px 16px -6px rgba(0, 0, 0, 0.06), 0 8px 20px -10px rgba(0, 0, 0, 0.04)', border: '1px solid rgba(0, 0, 0, 0.12)', maxHeight: '100%', pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.9 : 1, transition: 'opacity 0.2s', userSelect: loading ? 'none' : 'auto' }}>
              {/* TABLE BACKGROUND COLOR: Controls the faint grey background for the entire table (around the table border) */}
              <table className="w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.15)' }}>
                {/* THEAD BACKGROUND COLOR: Controls the faint grey background for the table header */}
                <thead ref={tableHeaderRef} className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                  <tr>
                    <th className="px-4 py-4 w-12">
                      <button
                        onClick={handleToggleSelectAll}
                        className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                        style={{
                          borderColor: selectedItems.size === lineItems.length && lineItems.length > 0 ? '#006de2' : '#d1d5db',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {selectedItems.size === lineItems.length && lineItems.length > 0 && (
                          <div
                            className="w-full h-full rounded"
                            style={{ backgroundColor: '#006de2' }}
                          />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Line item
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Code
                    </th>
                    <th className="px-6 py-4 text-left font-medium uppercase tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      QTY
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Unit
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Unit price
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Type
                    </th>
                    <th className="px-6 py-4 text-left font-medium tracking-wider" style={{ color: '#727272', fontSize: '14px' }}>
                      Total
                    </th>
                    <th className="px-6 py-4 w-8">
                    </th>
                  </tr>
                  <tr>
                    <th colSpan="9" className="p-0">
                      <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                    </th>
                  </tr>
                </thead>
                {/* TBODY BACKGROUND COLOR: Controls the faint grey background for the table body/rows */}
                <tbody style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                  <AnimatePresence>
                    {/* Always show existing items */}
                    {lineItems.map((item, index) => (
                      <React.Fragment key={`line-${index}`}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{
                          duration: 0.35,
                          ease: "easeOut",
                          delay: index * 0.07,
                        }}
                        className="cursor-pointer group"
                        onClick={() => handleEditItem(index)}
                        onMouseEnter={() => setHoveredRowIndex(index)}
                        onMouseLeave={() => setHoveredRowIndex(null)}
                      >
                        <td className="py-4 pl-4 pr-0" style={{ position: 'relative' }}>
                          <div
                            className="row-highlight"
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: '0.5rem',
                              right: 0,
                              pointerEvents: 'none',
                              zIndex: 0,
                              borderRadius: '8px 0 0 8px',
                              backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                              transition: 'background-color 0.15s ease',
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelect(index);
                            }}
                            className="w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors p-0.5"
                            style={{
                              borderColor: selectedItems.has(index) ? '#006de2' : '#d1d5db',
                              backgroundColor: 'transparent',
                              position: 'relative',
                              zIndex: 1,
                            }}
                          >
                            {selectedItems.has(index) && (
                              <div
                                className="w-full h-full rounded"
                                style={{ backgroundColor: '#006de2' }}
                              />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm relative" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          <div className="flex items-center gap-2">
                            {item.name}
                            {item.source === "ai" && (
                              <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-blue-100" style={{ color: '#424242' }}>
                                AI
                              </span>
                            )}
                            {/* Catalog Matches button - shows for AI items with matches */}
                            {item.source === "ai" && itemsWithMatches.has(index) && (
                              <button
                                ref={(el) => {
                                  if (el) {
                                    catalogButtonRefs.set(index, { current: el });
                                  } else {
                                    catalogButtonRefs.delete(index);
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveCatalogPopupIndex(activeCatalogPopupIndex === index ? null : index);
                                }}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs transition-colors ${
                                  activeCatalogPopupIndex === index 
                                    ? 'bg-green-100 ring-2 ring-green-300' 
                                    : 'bg-green-50 hover:bg-green-100'
                                }`}
                                style={{ color: '#059669', border: '1px solid #d1fae5' }}
                              >
                                Catalog Matches
                              </button>
                            )}
                          </div>
                          {/* Catalog Matches Popup - rendered via portal */}
                          <CatalogMatchesPopup
                            isOpen={activeCatalogPopupIndex === index}
                            triggerRef={catalogButtonRefs.get(index)}
                            lineItemName={item.name}
                            catalog={costCatalog}
                            onSelect={(catalogItem) => handleCatalogSelect(catalogItem, index)}
                            onClose={() => setActiveCatalogPopupIndex(null)}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {item.code || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {item.quantity || 0}
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {item.unit || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {item.unitPrice ? `$${item.unitPrice}` : "$0.00"}
                        </td>
                        <td className="px-6 py-4" style={{ backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          <span className="text-sm" style={{ color: '#424242' }}>
                            {item.category || "General"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm" style={{ color: '#424242', backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined, transition: 'background-color 0.15s ease' }}>
                          {item.quantity && item.unitPrice ? `$${(Number(item.quantity) * Number(item.unitPrice)).toFixed(2)}` : "$0.00"}
                        </td>
                        <td className="py-4 pr-4 pl-0" style={{ position: 'relative' }}>
                          <div
                            className="row-highlight-end"
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: 0,
                              right: '0.5rem',
                              pointerEvents: 'none',
                              zIndex: 0,
                              borderRadius: '0 8px 8px 0',
                              backgroundColor: (selectedItems.has(index) || hoveredRowIndex === index) ? '#edfaff' : undefined,
                              transition: 'background-color 0.15s ease',
                            }}
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(index);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                            style={{ position: 'relative', zIndex: 1 }}
                            title="Delete item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                      <tr>
                        <td colSpan="9" className="p-0">
                          <div className="mx-4" style={{ borderBottom: '1px solid #e8e8e8' }} />
                        </td>
                      </tr>
                      </React.Fragment>
                    ))}

                    {/* Show skeleton rows while loading */}
                    {loading && (
                      <>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <tr key={`skeleton-${index}`}>
                            <td className="px-4 py-4">
                              <Skeleton className="h-5 w-5 rounded-md" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-full max-w-[200px]" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-16" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-10" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-12" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-16" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-20" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-16" />
                            </td>
                            <td className="px-6 py-4">
                              <Skeleton className="h-4 w-4" />
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                  </AnimatePresence>

                  {/* Input row */}
                  <tr
                    ref={inputRowRef}
                    className={`${
                      isManualEntryDisabled
                        ? "opacity-60"
                        : ""
                    } transition-colors`}
                  >
                    <td className="px-4 py-4">
                      <div className="w-5 h-5 rounded-md border-2 border-gray-200" />
                    </td>
                    <td className="px-6 py-4 relative" ref={manualEntryTriggerRef}>
                      <div className="relative">
                        <input
                          ref={manualEntryInputRef}
                          type="text"
                          className="w-full bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed transition-colors placeholder-gray-400"
                          style={{ color: '#424242' }}
                          placeholder="Search or type line item..."
                          value={manualEntry.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setManualEntry({
                              ...manualEntry,
                              name: value,
                            });
                          }}
                          disabled={isManualEntryDisabled}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && manualEntry.name.trim() && !showManualEntryCatalogPopup) {
                              handleAddManualEntry();
                            }
                            if (e.key === "Escape" && showManualEntryCatalogPopup) {
                              setShowManualEntryCatalogPopup(false);
                            }
                          }}
                          onFocus={() => {
                            setIsInputRowActive(true);
                            // Expand layout when user starts typing in the table
                            if (!hasExpandedLayout) {
                              setHasExpandedLayout(true);
                              // Delay opening popup until layout animation completes
                              setTimeout(() => {
                                setShowManualEntryCatalogPopup(true);
                              }, LAYOUT_ANIMATION_DURATION * 1000);
                            } else {
                              // Layout already expanded, open popup immediately
                              setShowManualEntryCatalogPopup(true);
                            }
                          }}
                        />
                        
                        {/* Catalog Matches Popup for manual entry */}
                        <CatalogMatchesPopup
                          isOpen={showManualEntryCatalogPopup}
                          triggerRef={manualEntryTriggerRef}
                          lineItemName={manualEntry.name}
                          catalog={costCatalog}
                          onSelect={handleManualEntryCatalogSelect}
                          onClose={() => setShowManualEntryCatalogPopup(false)}
                          onSearchQueryChange={(newQuery) => {
                            setManualEntry((prev) => ({
                              ...prev,
                              name: newQuery,
                            }));
                          }}
                          focusInput={false}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        className="w-full bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed transition-colors placeholder-gray-400"
                        style={{ color: '#424242' }}
                        placeholder="Code"
                        value={manualEntry.code}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            code: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && manualEntry.name.trim()) {
                            handleAddManualEntry();
                          }
                        }}
                        onFocus={() => {
                          setIsInputRowActive(true);
                          // Expand layout when user starts typing in the table
                          if (!hasExpandedLayout) {
                            setHasExpandedLayout(true);
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        className="w-full bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed transition-colors placeholder-gray-400"
                        style={{ color: '#424242' }}
                        placeholder="0"
                        value={manualEntry.quantity}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            quantity: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                        onFocus={() => {
                          setIsInputRowActive(true);
                          // Expand layout when user starts typing in the table
                          if (!hasExpandedLayout) {
                            setHasExpandedLayout(true);
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        className="w-full bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed transition-colors placeholder-gray-400"
                        style={{ color: '#424242' }}
                        placeholder="Unit"
                        value={manualEntry.unit}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            unit: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                        onFocus={() => {
                          setIsInputRowActive(true);
                          // Expand layout when user starts typing in the table
                          if (!hasExpandedLayout) {
                            setHasExpandedLayout(true);
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        className="w-full bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed transition-colors placeholder-gray-400"
                        style={{ color: '#424242' }}
                        placeholder="$0.00"
                        value={manualEntry.unitPrice}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            unitPrice: e.target.value,
                          })
                        }
                        disabled={isManualEntryDisabled}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddManualEntry();
                        }}
                        onFocus={() => {
                          setIsInputRowActive(true);
                          // Expand layout when user starts typing in the table
                          if (!hasExpandedLayout) {
                            setHasExpandedLayout(true);
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative inline-flex items-center">
                        <select
                          className="appearance-none pr-6 bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed cursor-pointer"
                          style={{ color: manualEntry.category ? '#424242' : '#9ca3af' }}
                          value={manualEntry.category}
                          onChange={(e) =>
                            setManualEntry({
                              ...manualEntry,
                              category: e.target.value,
                            })
                          }
                          disabled={isManualEntryDisabled}
                          onFocus={() => {
                            setIsInputRowActive(true);
                            // Expand layout when user starts typing in the table
                            if (!hasExpandedLayout) {
                              setHasExpandedLayout(true);
                            }
                          }}
                        >
                          <option value="">Select type</option>
                          <option value="Material">Material</option>
                          <option value="Labor">Labor</option>
                          <option value="Equipment">Equipment</option>
                        </select>
                        <ChevronDown className="absolute right-0 w-4 h-4 pointer-events-none text-gray-400" />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                    </td>
                    <td className="px-6 py-4">
                    </td>
                  </tr>
                  
                  {/* Show preview row when input row is active */}
                  {isInputRowActive && (
                    <tr className="opacity-40 pointer-events-none">
                      <td className="px-4 py-4">
                        <div className="w-5 h-5 rounded-md border-2 border-gray-200" />
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">Line item...</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">Code</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">0</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">Unit</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">$0.00</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-400">Type</span>
                      </td>
                      <td className="px-6 py-4">
                      </td>
                      <td className="px-6 py-4">
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </motion.div>

        {/* Fixed bottom chatbox - centered initially, glides down on interaction */}
        <motion.div 
          ref={aiInputRef} 
          className="flex-shrink-0 px-6 md:px-8 pt-4 pb-1"
          initial={false}
          animate={{
            y: hasExpandedLayout ? 0 : 'calc(-25vh + 50px)',
          }}
          transition={{
            duration: LAYOUT_ANIMATION_DURATION,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          <AIInput
            value={input}
            onValueChange={setInput}
            onSubmit={handleGenerate}
            onVoiceRecordingComplete={handleVoiceRecordingComplete}
            isLoading={loading}
            isTranscribing={isTranscribing}
            showDisclaimer={hasGenerated}
          />
        </motion.div>

        {/* Flying items animation portal */}
        {flyingItems.length > 0 && createPortal(
          <div className="fixed inset-0 pointer-events-none z-[9999]">
            <AnimatePresence>
              {flyingItems.map((flyingItem) => (
                <motion.div
                  key={flyingItem.id}
                  initial={{
                    x: flyingItem.startX,
                    y: flyingItem.startY,
                    opacity: 0,
                    scale: 0.6,
                  }}
                  animate={{
                    x: flyingItem.endX,
                    y: flyingItem.endY,
                    opacity: [0, 1, 1, 0.8],
                    scale: [0.6, 1.1, 1, 1],
                  }}
                  transition={{
                    duration: 0.65,
                    delay: flyingItem.delay,
                    ease: [0.22, 0.61, 0.36, 1],
                    opacity: {
                      times: [0, 0.2, 0.8, 1],
                      duration: 0.65,
                      delay: flyingItem.delay,
                    },
                    scale: {
                      times: [0, 0.3, 0.7, 1],
                      duration: 0.65,
                      delay: flyingItem.delay,
                    },
                  }}
                  className="absolute"
                  style={{
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <span 
                    className="font-medium whitespace-nowrap"
                    style={{ 
                      color: '#006de2',
                      fontFamily: 'Geist, sans-serif',
                      fontSize: '15px',
                    }}
                  >
                    {flyingItem.label}
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>,
          document.body
        )}

        <AnimatePresence>
          {showEditPanel && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                onClick={handleCancelEdit}
              />

              {/* Side Panel */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
              >
                <div className="p-6 space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <h2 className="text-lg font-semibold" style={{ color: '#1C1C1C' }}>
                      Edit Line Item
                    </h2>
                    <button
                      onClick={handleCancelEdit}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Line Item Name *
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                        placeholder="Enter line item name"
                        value={editingItem?.name || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Code
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                        placeholder="Enter code"
                        value={editingItem?.code || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            code: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                          Quantity
                        </label>
                        <input
                          type="number"
                          className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                          placeholder="0"
                          value={editingItem?.quantity || ""}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              quantity: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                          Unit
                        </label>
                        <input
                          type="text"
                          className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                          placeholder="e.g., pcs, kg, m"
                          value={editingItem?.unit || ""}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              unit: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Unit Price
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                        placeholder="$0.00"
                        value={editingItem?.unitPrice || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            unitPrice: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                        Category
                      </label>
                      <div className="relative">
                        <select
                          className="w-full appearance-none px-3 py-2 pr-10 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{ color: '#424242', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}
                          value={editingItem?.category || ""}
                          onChange={(e) =>
                            setEditingItem({
                              ...editingItem,
                              category: e.target.value,
                            })
                          }
                        >
                          <option value="">Select type</option>
                          <option value="Material">Material</option>
                          <option value="Labor">Labor</option>
                          <option value="Equipment">Equipment</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#1C1C1C' }} />
                      </div>
                    </div>

                    {editingItem?.source && (
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: '#727272' }}>
                          Source
                        </label>
                        <div className="px-3 py-2 bg-gray-50 rounded-md text-sm" style={{ color: '#1C1C1C', border: '1px solid #e8e8e8' }}>
                          {editingItem.source === "ai" ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-100" style={{ color: '#1C1C1C' }}>
                                AI
                              </span>
                              AI Generated
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs bg-green-100" style={{ color: '#1C1C1C' }}>
                                Manual
                              </span>
                              Manually Added
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveEdit}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 hover:opacity-90 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
                      style={{ backgroundColor: '#006de2' }}
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex-1 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors border border-gray-300 shadow-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md border border-gray-200">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#1C1C1C' }}>
                Export to Project
              </h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium" style={{ color: '#1C1C1C' }}>
                    Choose Project
                  </label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="projectOption"
                        value="existing"
                        checked={!isCreatingNewProject}
                        onChange={() => {
                          setIsCreatingNewProject(false);
                          setSelectedProjectId("");
                          setProjectName("");
                          setError("");
                        }}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span style={{ color: '#1C1C1C' }}>Existing Project</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="projectOption"
                        value="new"
                        checked={isCreatingNewProject}
                        onChange={() => {
                          setIsCreatingNewProject(true);
                          setSelectedProjectId("");
                          setProjectName("");
                          setError("");
                        }}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span style={{ color: '#1C1C1C' }}>New Project</span>
                    </label>
                  </div>

                  {isCreatingNewProject ? (
                    <input
                      type="text"
                      className="w-full p-3 border border-gray-300 rounded-md bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      style={{ color: '#1C1C1C' }}
                      placeholder="Enter new project name"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <Select
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (p) => p.value === selectedProjectId
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        setSelectedProjectId(
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      placeholder="Search or select a project..."
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  )}

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm mt-2" style={{ color: '#1C1C1C' }}>
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    onClick={handleExport}
                    disabled={
                      exportLoading ||
                      (isCreatingNewProject
                        ? !projectName.trim()
                        : !selectedProjectId)
                    }
                  >
                    {exportLoading ? "Exporting..." : "Export"}
                  </button>
                  <button
                    className="flex-1 bg-white hover:bg-gray-50 px-4 py-2 rounded-md text-gray-700 text-sm font-medium transition-colors border border-gray-300 shadow-sm disabled:opacity-50"
                    onClick={() => {
                      setShowExportModal(false);
                      setError("");
                    }}
                    disabled={exportLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
